import React from 'react';
import { FileText, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { STEPS } from '../data';

const styles = {
  wrap: {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    animation: 'fadeUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) both',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  iconWrap: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'rgba(34, 197, 94, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    color: '#22c55e',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-dim)',
    marginBottom: '28px',
  },
  progressContainer: {
    marginBottom: '24px',
  },
  track: {
    height: '8px',
    background: 'var(--surface3)',
    borderRadius: '999px',
    overflow: 'hidden',
    position: 'relative',
  },
  fill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
    borderRadius: '999px',
    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 0 12px rgba(34, 197, 94, 0.4)',
  }),
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
  },
  statusLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-dim)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  pctText: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#22c55e',
    fontFamily: 'var(--font-display)',
  },
  footerBadge: {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: 'var(--text-dim)',
    fontSize: '12px',
  },
  dots: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  dot: (filled) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: filled ? '#22c55e' : 'var(--surface3)',
    transition: 'all 0.3s ease',
    transform: filled ? 'scale(1.2)' : 'scale(1)',
  }),
};

export default function ProgressBar({ progress, totalFiles = 1, currentFile = 1 }) {
  const stepIndex = Math.min(Math.floor((progress / 100) * STEPS.length), STEPS.length - 1);
  const label = STEPS[stepIndex];
  const isComplete = progress >= 100;

  return (
    <div style={styles.wrap}>
      <div style={styles.iconWrap}>
        {isComplete ? <CheckCircle2 size={32} /> : <FileText size={32} />}
      </div>

      <div style={styles.title}>
        {isComplete ? 'Almost Done!' : 'Converting your files...'}
      </div>
      <div style={styles.subtitle}>
        {isComplete 
          ? 'Your files have been successfully processed.' 
          : 'Please don\'t close this window. Large files may take a few moments.'}
      </div>

      <div style={styles.dots}>
        {STEPS.map((_, i) => (
          <div key={i} style={styles.dot(i <= stepIndex)} />
        ))}
      </div>

      <div style={styles.progressContainer}>
        <div style={styles.track}>
          <div style={styles.fill(progress)} />
        </div>
        <div style={styles.infoRow}>
          <span style={styles.statusLabel}>{label}</span>
          <span style={styles.pctText}>{Math.round(progress)}%</span>
        </div>
      </div>

      {totalFiles > 1 && !isComplete && (
        <div style={{ fontSize: '13px', color: 'var(--text-dim)', marginBottom: '16px' }}>
          Processing {currentFile} of {totalFiles} files
        </div>
      )}

      <div style={styles.footerBadge}>
        <ShieldCheck size={16} color="#22c55e" />
        <span>Files are processed securely and never shared with third parties</span>
      </div>
    </div>
  );
}
